import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert
} from '@mui/material';
import { updateUserSubscription } from '../services/users';

// Subscription types list
const SUBSCRIPTION_TYPES = [
  { value: 0, label: 'Free', description: 'Basic free subscription (up to 10 recommendations)' },
  { value: 1, label: 'Premium 1', description: 'Premium level 1 (up to 20 recommendations + balance bonus)' },
  { value: 2, label: 'Premium 2', description: 'Premium level 2 (up to 20 recommendations + balance bonus)' },
  { value: 3, label: 'Premium 3', description: 'Premium level 3 (up to 20 recommendations + balance bonus)' },
  { value: 4, label: 'Premium 4', description: 'Premium level 4 (up to 20 recommendations + balance bonus)' },
  { value: 5, label: 'Referral', description: 'Special subscription for referring 2+ users (up to 20 recommendations)' }
];

/**
 * User subscription management component
 */
const SubscriptionEditor = ({ user, onSubscriptionUpdated }) => {
  const [subscriptionType, setSubscriptionType] = useState(user?.subscribtion?.type || 0);
  const [subscriptionData, setSubscriptionData] = useState(user?.subscribtion?.data || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Convert timestamp to readable format
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Never';
    
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString();
    }
    
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleString();
    }
    
    if (timestamp instanceof Date) {
      return timestamp.toLocaleString();
    }
    
    return 'Unknown';
  };

  // Handle subscription type change
  const handleTypeChange = (e) => {
    setSubscriptionType(Number(e.target.value));
    // Reset messages
    setError('');
    setSuccess(false);
  };

  // Handle subscription data change
  const handleDataChange = (e) => {
    setSubscriptionData(e.target.value);
    // Reset messages
    setError('');
    setSuccess(false);
  };

  // Open confirmation dialog
  const handleOpenConfirm = () => {
    setConfirmOpen(true);
  };

  // Close confirmation dialog
  const handleCloseConfirm = () => {
    setConfirmOpen(false);
  };

  // Apply subscription changes
  const handleApplySubscription = async () => {
    setConfirmOpen(false);
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await updateUserSubscription(user.id, subscriptionType, subscriptionData);
      setSuccess(true);
      
      // Notify parent component about the update if callback provided
      if (typeof onSubscriptionUpdated === 'function') {
        onSubscriptionUpdated({
          ...user,
          subscribtion: {
            type: subscriptionType,
            data: subscriptionData,
            created: new Date() // temporary value, will be updated on the server
          }
        });
      }
    } catch (err) {
      setError(err.message || 'An error occurred while updating the subscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="h6" gutterBottom>
        Subscription Management
      </Typography>

      {/* Current subscription information */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2">Current subscription:</Typography>
        <Typography>
          <strong>Type:</strong> {
            SUBSCRIPTION_TYPES.find(t => t.value === (user?.subscribtion?.type || 0))?.label || 'Unknown type'
          }
        </Typography>
        {user?.subscribtion?.data && (
          <Typography component="div">
            <strong>Data:</strong>
            <Box 
              sx={{ 
                mt: 0.5,
                p: 1, 
                bgcolor: 'background.paper', 
                border: '1px solid', 
                borderColor: 'divider',
                borderRadius: 1,
                maxWidth: '100%',
                overflowX: 'auto',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap'
              }}
            >
              {user.subscribtion.data}
            </Box>
          </Typography>
        )}
        {user?.subscribtion?.created && (
          <Typography>
            <strong>Created:</strong> {formatDate(user.subscribtion.created)}
          </Typography>
        )}
      </Box>
      
      {/* Subscription edit form */}
      <Box>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="subscription-type-label">Subscription Type</InputLabel>
          <Select
            labelId="subscription-type-label"
            id="subscription-type"
            value={subscriptionType}
            label="Subscription Type"
            onChange={handleTypeChange}
            disabled={loading}
          >
            {SUBSCRIPTION_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label} - {type.description}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Additional Subscription Data"
          value={subscriptionData}
          onChange={handleDataChange}
          disabled={loading}
          multiline
          minRows={3}
          maxRows={8}
          sx={{ mb: 2 }}
          helperText="For most subscription types, this field can be empty or used for technical data"
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Subscription updated successfully!
          </Alert>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenConfirm}
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Updating...' : 'Update Subscription'}
        </Button>
      </Box>

      {/* Confirmation dialog */}
      <Dialog open={confirmOpen} onClose={handleCloseConfirm}>
        <DialogTitle>Confirm Subscription Change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to change the user's subscription type to {
              SUBSCRIPTION_TYPES.find(t => t.value === subscriptionType)?.label || 'Unknown type'
            }. 
            {subscriptionType !== 0 && ' The user will also receive +1000 to their rating balance.'}
            
            Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} color="primary">
            Cancel
          </Button>
          <Button onClick={handleApplySubscription} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionEditor;